/**
 * fuarAlaniEkBilgiService.js — the WRITE calls of a fairground's extra data: profile (künye, address, map pin), logo, photo
 * gallery and organisation chart (backlog #51 follow-up, 2026-10-20; backend #99).
 *
 * LIVE (no mock). READING is not here: `GET /fuar/alani/{id}` (fuarAlaniSalonService.getFuarAlani) already carries the logo, künye,
 * address, pin, `photos[]` and the `orgChart` tree, and the detail page reloads it after every write. That is also why no
 * call here returns anything the page needs: the write answers differ in shape (the photo upload answers the whole venue, the other
 * photo writes the gallery, the chart writes the tree), and re-reading is one request that always has the same shape.
 *
 * Every write needs `page.fuar.yonetim` on the backend (the page hides the controls without it, the backend checks again).
 * Contract: FuarController /alani/{id}/profile, /logo, /photos, /org-chart; notes in FDS-Backend fds-features
 * (fairgrounds-halls-height-limits.md, section #99).
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/fuar/alani`;

/** The image types the backend accepts for a logo and a photo (SVG is NOT accepted; the backend also checks the bytes and the size). */
export const GORSEL_UZANTILAR = ['png', 'jpg', 'jpeg', 'webp'];

/** An early, cheap check before uploading. The size limit is a system setting (Dosya Ayarları), so it is left to the backend. */
export function gorselUzantisiGecerli(file) {
  const ext = String(file?.name || '').split('.').pop()?.toLowerCase();
  return GORSEL_UZANTILAR.includes(ext);
}

/**
 * A sentence for the screen out of a failed call: the backend's own message when it sent one (400 / 403 / 404 / 409 all do, in
 * the request's language for a refusal), else the 401 text, else `fallback`.
 */
export function hataMetni(error, fallback) {
  const data = error?.response?.data;
  if (data && typeof data === 'object' && data.message) return data.message;
  if (typeof data === 'string' && data.trim()) return data;
  if (error?.response?.status === 401) return i18n.t("services:firmaMediaService.oturumGecersiz401LutfenYeniden");
  return error?.message || fallback;
}

/** A GenericResultJson failure can arrive as HTTP 200 + result:false: count it as an error too. */
function unwrap(data, fallback) {
  if (data && typeof data === 'object' && data.result === false) throw new Error(data.message || fallback);
  return data;
}

const FALLBACK = () => i18n.t("common:bilinmeyenHata");

// ─── Profile and logo ───────────────────────────────────────────────────────

/**
 * PUT /fuar/alani/{id}/profile — body {kunye, address, latitude, longitude}; the backend changes only the keys that are SENT, and
 * a blank text clears it. latitude and longitude go together: both numbers, or both null to remove the pin.
 */
export async function updateFuarAlaniProfile(fuarAlaniId, { kunye, address, latitude, longitude }) {
  const res = await axios.put(`${BASE}/${fuarAlaniId}/profile`, { kunye, address, latitude, longitude }, { headers: getAuthHeader() });
  return unwrap(res.data, FALLBACK());
}

/** POST /fuar/alani/{id}/logo — multipart `file`. */
export async function uploadFuarAlaniLogo(fuarAlaniId, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${BASE}/${fuarAlaniId}/logo`, form, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res.data, FALLBACK());
}

// ─── Photos ─────────────────────────────────────────────────────────────────

/** POST /fuar/alani/{id}/photos — multipart `file` + optional `baslik`. The first photo becomes the cover; at most 20 per venue. */
export async function uploadFuarAlaniPhoto(fuarAlaniId, file, baslik) {
  const form = new FormData();
  form.append('file', file);
  if (baslik && baslik.trim()) form.append('baslik', baslik.trim());
  const res = await axios.post(`${BASE}/${fuarAlaniId}/photos`, form, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res.data, FALLBACK());
}

/** PUT /fuar/alani/{id}/photos/{fotoId} — only the keys sent change: `baslik` (blank clears it), `sira`, `kapak: true`. */
export async function updateFuarAlaniPhoto(fuarAlaniId, fotoId, patch) {
  const res = await axios.put(`${BASE}/${fuarAlaniId}/photos/${fotoId}`, patch, { headers: getAuthHeader() });
  return unwrap(res.data, FALLBACK());
}

/** PUT /fuar/alani/{id}/photos/{fotoId}/cover — one cover per venue. */
export async function setFuarAlaniPhotoCover(fuarAlaniId, fotoId) {
  const res = await axios.put(`${BASE}/${fuarAlaniId}/photos/${fotoId}/cover`, null, { headers: getAuthHeader() });
  return unwrap(res.data, FALLBACK());
}

/** DELETE /fuar/alani/{id}/photos/{fotoId} — a soft delete (the file stays on disk); deleting the cover promotes the next photo. */
export async function deleteFuarAlaniPhoto(fuarAlaniId, fotoId) {
  const res = await axios.delete(`${BASE}/${fuarAlaniId}/photos/${fotoId}`, { headers: getAuthHeader() });
  return unwrap(res.data, FALLBACK());
}

// ─── Organisation chart ─────────────────────────────────────────────────────

/** POST /fuar/alani/{id}/org-chart — {adSoyad (required), unvan, telefon, email, parentId (null = top level)}; lands last among its siblings. */
export async function addFuarAlaniOrgNode(fuarAlaniId, { adSoyad, unvan, telefon, email, parentId }) {
  const res = await axios.post(`${BASE}/${fuarAlaniId}/org-chart`, { adSoyad, unvan, telefon, email, parentId }, { headers: getAuthHeader() });
  return unwrap(res.data, FALLBACK());
}

/** PUT /fuar/alani/{id}/org-chart/{nodeId} — only the keys sent change; a new parentId that would make a cycle is refused (400). */
export async function updateFuarAlaniOrgNode(fuarAlaniId, nodeId, patch) {
  const res = await axios.put(`${BASE}/${fuarAlaniId}/org-chart/${nodeId}`, patch, { headers: getAuthHeader() });
  return unwrap(res.data, FALLBACK());
}

/** DELETE /fuar/alani/{id}/org-chart/{nodeId} — refused (409) while the node still has people under it. */
export async function deleteFuarAlaniOrgNode(fuarAlaniId, nodeId) {
  const res = await axios.delete(`${BASE}/${fuarAlaniId}/org-chart/${nodeId}`, { headers: getAuthHeader() });
  return unwrap(res.data, FALLBACK());
}
